import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useGridApiRef, GridRow } from '@mui/x-data-grid';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import styled from 'styled-components';
import { Person, Row } from 'talent_matching/types';
import HeaderRow from 'talent_matching/components/header_row';
import FilterRow from 'talent_matching/components/filters/filter_row';
import CandidateSidePanel from 'talent_matching/components/side_panel/candidate_side_panel';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { SidePanelHandle } from 'shared/components/side_panel';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import FullWidth from '@grnhse/seedling/lib/birch/layouts/full_width';
import { RowDef } from '@grnhse/seedling/lib/birch/components/table/types';
import { CellParamsDef } from '@grnhse/seedling/lib/birch/components/table';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import {
  GridRowSelectionModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import { SelectionsHelper } from 'talent_matching/hooks/use_selections_helper';
import { useRankingInProgress } from 'talent_matching/contexts/ranking_in_progress_context';
import RankingLoader from 'talent_matching/components/table/ranking_loader';
import CalibrationModal from 'talent_matching/components/calibration/calibration_modal';
import NoResultsOverlay from 'talent_matching/components/no_results_overlay';
import useQueryHelper from 'talent_matching/hooks/use_query_helper';
import { usePerson } from 'talent_matching/hooks/use_people';
import { useColumns } from 'talent_matching/components/table/columns';
import BaseDataTable from 'talent_matching/components/table/base_data_table';
import DataViewTableFooter from 'talent_matching/components/table/data_view_table_footer';
import { useColumnState } from 'talent_matching/hooks/use_column_state';
import useStickyGridHeader from 'talent_matching/hooks/use_sticky_grid_header';
import { RejectModalProvider } from '../../../application_review/components/reject_modal_context';
import { useQueryClient } from '@tanstack/react-query';
import { buildQuestionColumnsFromPeople } from 'talent_matching/components/table/question_columns';
import { ACTIONS_COLUMN_FIELD } from 'talent_matching/components/table/column_visibility_defaults';
import { AdvancedCandidateCountProvider } from 'talent_matching/contexts/advanced_candidate_count_context';

// Columns that should not be togglable (always visible)
const NON_TOGGLABLE_COLUMNS = ['fullName', ACTIONS_COLUMN_FIELD];

type Props = {
  rows: Row[];
  totalRows: number;
  loading: boolean;
  rowsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onRowSelected: (selectedRows: GridRowSelectionModel) => void;
  selectionsHelper: SelectionsHelper;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  preSelectedRow: Row | null;
  setPreSelectedRow: (row: Row | null) => void;
};

export const MinHeightStack = styled(Stack)`
  min-height: 750px;
`;

const getCandidatePrefetchBatch = (
  rows: Row[],
  selectedRowIndex: number,
  batchRadius: number // number of candidates before and after selection
) => {
  const startIndex = Math.max(0, selectedRowIndex - batchRadius);
  const endIndex = Math.min(rows.length, selectedRowIndex + batchRadius + 1);

  return [
    ...rows.slice(startIndex, selectedRowIndex),
    ...rows.slice(selectedRowIndex + 1, endIndex),
  ];
};

const Wrapper = styled.div`
  .highlighted-row {
    background-color: ${FullPalette.GREY_E500};
  }
`;

// This SyncedRow injects the latest person from the row
// cache to keep data in sync with the side panel.
const SyncedRow = (props: React.ComponentProps<typeof GridRow>) => {
  const { person } = usePerson(props.row.id);

  return <GridRow {...props} row={person} />;
};

const DataViewTable = ({
  rows,
  totalRows,
  loading,
  rowsPerPage,
  currentPage,
  onPageChange,
  onRowSelected,
  selectionsHelper,
  columnVisibilityModel,
  setColumnVisibilityModel,
  preSelectedRow,
  setPreSelectedRow,
}: Props) => {
  const { canBulkEdit } = useTalentMatchingTabContext();

  const [showCalibrationModal, openCalibrationModal, closeCalibrationModal] =
    useModal();
  const apiRef = useGridApiRef();

  const queryClient = useQueryClient();
  const questionColumns = useMemo(() => {
    const people = rows
      .map(
        (row) =>
          queryClient.getQueryData(['person', row.id]) as Person | undefined
      )
      .filter((person): person is Person => !!person);

    return buildQuestionColumnsFromPeople(people);
  }, [queryClient, rows]);

  const missingQuestionFields = useMemo(() => {
    return questionColumns
      .map((c) => c.field)
      .filter(
        (field) =>
          !Object.prototype.hasOwnProperty.call(columnVisibilityModel, field)
      );
  }, [columnVisibilityModel, questionColumns]);

  const columnVisibilityModelWithQuestionDefaults = useMemo(() => {
    if (missingQuestionFields.length === 0) {
      return columnVisibilityModel;
    }

    const missingFieldsDefaults = missingQuestionFields.reduce(
      (obj: GridColumnVisibilityModel, field) => {
        obj[field] = false;
        return obj;
      },
      {}
    );

    return {
      ...columnVisibilityModel,
      ...missingFieldsDefaults,
    };
  }, [columnVisibilityModel, missingQuestionFields]);

  useEffect(() => {
    if (missingQuestionFields.length === 0) {
      return;
    }

    setColumnVisibilityModel(columnVisibilityModelWithQuestionDefaults);
  }, [
    columnVisibilityModelWithQuestionDefaults,
    missingQuestionFields.length,
    setColumnVisibilityModel,
  ]);

  const { handleColumnChange } = useColumnState(
    apiRef,
    columnVisibilityModelWithQuestionDefaults
  );

  const { excludePeople } = useQueryHelper();

  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(!!preSelectedRow);
  const [selectedRow, setSelectedRow] = useState<Row | null>(null);
  const selectedRowIndex =
    selectedRow && rows.findIndex((row) => row.id === selectedRow.id);
  const previousRow =
    selectedRowIndex !== null && selectedRowIndex > 0
      ? rows[selectedRowIndex - 1]
      : null;
  const nextRow =
    selectedRowIndex !== null && selectedRowIndex < rows.length - 1
      ? rows[selectedRowIndex + 1]
      : null;
  const candidatePrefetchBatch =
    selectedRowIndex !== null
      ? getCandidatePrefetchBatch(rows, selectedRowIndex, 2)
      : [];

  const popoverRef = useRef<SidePanelHandle | null>(null);

  useEffect(() => {
    if (preSelectedRow && rows.some((row) => row.id === preSelectedRow.id)) {
      setSelectedRow(preSelectedRow);
      setIsPanelOpen(true);
      setPreSelectedRow(null);
    }
  }, [rows, preSelectedRow, setPreSelectedRow]);

  const handlePanelToggle = () => {
    if (isPanelOpen) {
      popoverRef.current?.close();
    } else {
      setIsPanelOpen(true);
    }
  };

  const handleRowClick = (row: Row) => {
    if (!row || !row.id) {
      return;
    }

    // If clicking the same row, toggle the panel
    if (selectedRow?.id === row.id) {
      if (isPanelOpen) {
        popoverRef.current?.close();
      } else {
        setIsPanelOpen(true);
      }
    } else {
      // Different row - update content and show panel
      setSelectedRow(row);
      setIsPanelOpen(true);
    }
  };

  const { rankingInProgress, isBackgroundRanking } = useRankingInProgress();
  const userInitiatedRankingInProgress =
    rankingInProgress && !isBackgroundRanking;
  const { wrapperRef } = useStickyGridHeader({
    disabled: userInitiatedRankingInProgress,
  });

  const onRejectSuccess = (row: Row, closeModal: () => void) => {
    closeModal();

    if (isPanelOpen && row.id === selectedRow?.id) {
      if (nextRow || previousRow) {
        setPreSelectedRow(nextRow || previousRow || null);
      } else {
        setIsPanelOpen(false);
        setSelectedRow(null);
      }
    }

    excludePeople([row.id]);
  };

  const getRowClassName = useCallback(
    (params: CellParamsDef) => {
      return isPanelOpen && selectedRow?.id === params.row.id
        ? 'highlighted-row'
        : '';
    },
    [isPanelOpen, selectedRow?.id]
  );

  const columns = useColumns({
    selectionsHelper,
    isPanelOpen,
    selectedRow,
    openCalibrationModal,
    onRejectSuccess,
    questionColumns,
  });

  const columnSelectorColumns = useMemo(
    () =>
      columns
        .filter((c) => !NON_TOGGLABLE_COLUMNS.includes(c.field))
        .map((c) => ({
          field: c.field,
          label: c.headerName || c.field,
        })),
    [columns]
  );

  return (
    <AdvancedCandidateCountProvider openCalibrationModal={openCalibrationModal}>
      <FullWidth>
        <MinHeightStack spacing={2}>
          <HeaderRow
            selectionsHelper={selectionsHelper}
            apiRef={apiRef}
            openCalibrationModal={openCalibrationModal}
            columnSelectorColumns={columnSelectorColumns}
            columnVisibilityModel={columnVisibilityModelWithQuestionDefaults}
            setColumnVisibilityModel={setColumnVisibilityModel}
          />
          {canBulkEdit && <FilterRow />}
          {userInitiatedRankingInProgress ? (
            <RankingLoader />
          ) : (
            <Wrapper ref={wrapperRef}>
              <BaseDataTable
                rows={rows}
                columns={columns}
                totalRows={totalRows}
                loading={loading}
                rowsPerPage={rowsPerPage}
                currentPage={currentPage}
                onPageChange={onPageChange}
                onRowSelected={onRowSelected}
                apiRef={apiRef}
                columnVisibilityModel={
                  columnVisibilityModelWithQuestionDefaults
                }
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                onColumnWidthChange={handleColumnChange}
                onRowClick={(row: RowDef) => handleRowClick(row as Person)}
                getRowClassName={getRowClassName}
                footer={() => <DataViewTableFooter rowsPerPage={rowsPerPage} />}
                slots={{
                  noRowsOverlay: NoResultsOverlay,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  row: SyncedRow as React.ComponentType<any>,
                }}
              />
            </Wrapper>
          )}
        </MinHeightStack>

        {selectedRow && (
          <RejectModalProvider>
            <CandidateSidePanel
              ref={popoverRef}
              onClose={() => setIsPanelOpen(false)}
              onToggle={handlePanelToggle}
              applicationId={selectedRow.id}
              isVisible={isPanelOpen}
              currentIndex={selectedRowIndex}
              rowCount={rows.length}
              previousCandidate={previousRow}
              nextCandidate={nextRow}
              candidatePrefetchBatch={candidatePrefetchBatch}
              selectCandidate={handleRowClick}
              openCalibrationModal={openCalibrationModal}
              calibrationModalOpen={showCalibrationModal}
              onRejectSuccess={onRejectSuccess}
              onAdvanceSuccess={() => {
                if (isPanelOpen) {
                  setPreSelectedRow(nextRow || previousRow || null);

                  if (!nextRow && !previousRow) {
                    setIsPanelOpen(false);
                  }
                }
              }}
            />
          </RejectModalProvider>
        )}

        <CalibrationModal
          isOpen={showCalibrationModal}
          onClose={closeCalibrationModal}
        />
      </FullWidth>
    </AdvancedCandidateCountProvider>
  );
};

export default DataViewTable;
